import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Rocket, Database, ShoppingCart } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useAppContext } from '@/contexts/AppContext';

const StrategyDirection = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useAppContext();

  const steps = [
    {
      number: '1',
      title: t('strategyDirection.steps.step1.title'),
      description: t('strategyDirection.steps.step1.description')
    },
    {
      number: '2',
      title: t('strategyDirection.steps.step2.title'),
      description: t('strategyDirection.steps.step2.description')
    },
    {
      number: '3',
      title: t('strategyDirection.steps.step3.title'),
      description: t('strategyDirection.steps.step3.description')
    }
  ];

  const modules = [
    {
      icon: <Database className="h-12 w-12 text-white" />,
      title: t('strategyDirection.modules.module1.title'),
      description: t('strategyDirection.modules.module1.description'),
      bulletPoints: t<string[]>('strategyDirection.modules.module1.bulletPoints', { returnObjects: true }),
      highlight: t('strategyDirection.modules.module1.highlight'),
      color: 'bg-blue-600'
    },
    {
      icon: <Rocket className="h-12 w-12 text-white" />,
      title: t('strategyDirection.modules.module2.title'),
      description: t('strategyDirection.modules.module2.description'),
      bulletPoints: t<string[]>('strategyDirection.modules.module2.bulletPoints', { returnObjects: true }),
      highlight: t('strategyDirection.modules.module2.highlight'),
      color: 'bg-blue-700'
    },
    {
      icon: <ShoppingCart className="h-12 w-12 text-white" />,
      title: t('strategyDirection.modules.module3.title'),
      description: t('strategyDirection.modules.module3.description'),
      bulletPoints: t<string[]>('strategyDirection.modules.module3.bulletPoints', { returnObjects: true }),
      highlight: t('strategyDirection.modules.module3.highlight'),
      color: 'bg-blue-800'
    }
  ];

  return (
    <section 
      id="strategie" 
      className={`section ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white'}`}
    >
      <div className="container">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            🚀 {t('strategyDirection.title')}
          </h2>
          <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} max-w-3xl mx-auto`}>
            {t('strategyDirection.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {steps.map((step, index) => (
            <div key={index} className="relative">
              <div className="absolute -top-4 -left-4 bg-orange-500 text-white h-10 w-10 rounded-full flex items-center justify-center text-xl font-bold">
                {step.number}
              </div>
              <Card className={`h-full ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'border-gray-200'} shadow-md`}>
                <CardContent className="p-6 pt-8">
                  <h3 className="text-xl font-bold mb-3">📌 {t('strategyDirection.stepPrefix')} {step.number}: {step.title}</h3>
                  <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{step.description}</p>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>

        <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} p-8 rounded-2xl mb-16`}>
          <p className="text-lg text-center mb-8">
            {t('strategyDirection.moduleIntro')}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {modules.map((module, index) => (
              <Card key={index} className={`${module.color} text-white border-none`}>
                <CardContent className="p-6">
                  <div className="mb-4">
                    {module.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{module.title}</h3>
                  <p className="text-white font-medium mb-3">{module.description}</p>
                  
                  <ul className="list-disc pl-5 mb-4 space-y-1 text-white/90">
                    {module.bulletPoints.map((point, i) => (
                      <li key={i}>{point}</li>
                    ))}
                  </ul>
                  
                  {module.highlight && (
                    <div className="mt-4 p-3 bg-white/10 rounded-md">
                      <p className="text-white font-medium text-sm">
                        {module.highlight}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <div className="max-w-3xl mx-auto text-center">
          <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700">
            <a href="#contact">{t('strategyDirection.ctaText')}</a>
          </Button>
          
          <p className={`mt-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'} font-medium`}>
            {t('strategyDirection.ctaDescription')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default StrategyDirection; 